import { queryOptions, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { api } from '@/lib/api';
import { queryKeys } from './query-keys';
import type {
  DeckGroup,
  DeckGroupFilters,
  CreateDeckGroupRequest,
  UpdateDeckGroupRequest,
} from '@/types/deck-group';

// Query options for a single deck group
export const deckGroupQueryOptions = (id: string) =>
  queryOptions({
    queryKey: queryKeys.deckGroups.detail(id),
    queryFn: async () => {
      const response = await api.getDeckGroup(id);
      return response.deckGroup;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

// Hook to fetch all deck groups with optional filters
export function useDeckGroups(filters?: DeckGroupFilters) {
  return useQuery({
    queryKey: queryKeys.deckGroups.list(filters || {}),
    queryFn: async () => {
      const response = await api.getDeckGroups(filters);
      return response.deckGroups;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// Hook to fetch a single deck group
export function useDeckGroup(id: string) {
  return useQuery(deckGroupQueryOptions(id));
}

// Hook to create a deck group
export function useCreateDeckGroup() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (data: CreateDeckGroupRequest) => api.createDeckGroup(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.deckGroups.lists(),
      });
      navigate({
        to: '/quizlets',
        search: { tab: 'grouped' },
      });
    },
  });
}

// Hook to update a deck group
export function useUpdateDeckGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateDeckGroupRequest }) =>
      api.updateDeckGroup(id, data),
    // Optimistic updates for instant UI feedback
    onMutate: async ({ id, data }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: queryKeys.deckGroups.detail(id),
      });

      // Snapshot previous value
      const previous = queryClient.getQueryData<DeckGroup>(
        queryKeys.deckGroups.detail(id)
      );

      // Optimistically update
      if (previous) {
        queryClient.setQueryData<DeckGroup>(
          queryKeys.deckGroups.detail(id),
          { ...previous, ...data }
        );
      }

      return { previous, id };
    },
    onError: (_err, _variables, context) => {
      // Rollback on error
      if (context?.previous) {
        queryClient.setQueryData(
          queryKeys.deckGroups.detail(context.id),
          context.previous
        );
      }
    },
    onSuccess: (_, { id }) => {
      // Invalidate to ensure we have latest data
      queryClient.invalidateQueries({
        queryKey: queryKeys.deckGroups.detail(id),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.deckGroups.lists(),
      });
    },
  });
}

// Hook to delete a deck group
export function useDeleteDeckGroup() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (id: string) => api.deleteDeckGroup(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.deckGroups.lists(),
      });
      navigate({
        to: '/quizlets',
        search: { tab: 'grouped' },
      });
    },
  });
}

// Hook to get deck group with questions for starting a session
export function useDeckGroupSession(id: string, enabled = false) {
  return useQuery({
    queryKey: queryKeys.deckGroups.session(id),
    queryFn: async () => {
      const response = await api.startDeckGroupSession(id);
      return response.deckGroup;
    },
    enabled,
    staleTime: 0, // Always fresh for sessions
  });
}

// Hook to start a deck group session (mutation version for imperative calls)
export function useStartDeckGroupSession() {
  return useMutation({
    mutationFn: (id: string) => api.startDeckGroupSession(id),
  });
}
